// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import Router from 'next/router';
import { imageFileExtensions } from '../../../constants/imageFileExtensions';
import { grispiNotification } from '../../../store/useNotificationStore';
import { generateTicketFormName } from '../../../utils/forms/formHelpers';
import LoadingImage from '../blots/loadingImage';

const getUploadControllerElement = () =>
    document
        .getElementById(`${generateTicketFormName(Router.router.pathname, Router.router.query)}-attachments`)
        ?.getElementsByTagName('input')?.[0];

type UploadedFile = { id: number; url: string };

// inspired from https://github.com/NoelOConnell/quill-image-uploader/blob/master/src/quill.imageUploader.js
class GrispiUploader {
    options: { upload: (file: File) => Promise<UploadedFile> };
    constructor(quill, options: { upload: (file: File) => Promise<UploadedFile> }) {
        this.quill = quill;
        this.options = options;
        this.range = null;

        if (typeof this.options.upload !== 'function')
            console.warn('[Missing config] upload function that returns a promise is required');

        const toolbar = this.quill.getModule('toolbar');
        toolbar?.addHandler('image', this.selectLocalImage.bind(this));

        this.handleDrop = this.handleDrop.bind(this);
        this.handlePaste = this.handlePaste.bind(this);

        this.quill.root.addEventListener('drop', this.handleDrop, false);
        this.quill.root.addEventListener('paste', this.handlePaste, false);
    }

    selectLocalImage() {
        this.quill.focus();
        this.range = this.quill.getSelection();
        const uploadControllerElement = getUploadControllerElement();
        if (!uploadControllerElement) {
            // console.debug('--------------------------');
            // console.debug('No upload controller element, check upload feature is available');
            // console.debug('--------------------------');
            return;
        }
        // trigger antd-upload onClick event
        uploadControllerElement.click();
    }

    handleDrop(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        if (evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files.length) {
            if (document.caretRangeFromPoint) {
                const selection = document.getSelection();
                const range = document.caretRangeFromPoint(evt.clientX, evt.clientY);
                if (selection && range) {
                    selection.setBaseAndExtent(
                        range.startContainer,
                        range.startOffset,
                        range.startContainer,
                        range.startOffset
                    );
                }
            } else {
                const selection = document.getSelection();
                const range = document.caretPositionFromPoint(evt.clientX, evt.clientY);
                if (selection && range) {
                    selection.setBaseAndExtent(range.offsetNode, range.offset, range.offsetNode, range.offset);
                }
            }

            this.quill.focus();
            this.range = this.quill.getSelection();
            const file = evt.dataTransfer.files[0];

            if (!imageFileExtensions.includes(file.type)) {
                return grispiNotification('error', 'You can drop only images in comment');
            }
            setTimeout(() => {
                this.quill.focus();
                this.range = this.quill.getSelection();
                this.readAndUploadFile(file);
            }, 0);
        }
    }

    handlePaste(evt) {
        const clipboard = evt.clipboardData || window.clipboardData;

        // IE 11 is .files other browsers are .items
        if (clipboard && (clipboard.items || clipboard.files)) {
            const items = clipboard.items || clipboard.files;

            for (let i = 0; i < items.length; i++) {
                const file = items[i].getAsFile ? items[i].getAsFile() : items[i];

                if (file) {
                    this.quill.focus();
                    this.range = this.quill.getSelection();
                    evt.preventDefault();
                    if (!imageFileExtensions.includes(file?.type)) {
                        return grispiNotification('error', 'You can paste only images in comment');
                    }
                    setTimeout(() => {
                        this.quill.focus();
                        this.range = this.quill.getSelection();
                        this.readAndUploadFile(file);
                    }, 0);
                }
            }
        }
    }

    readAndUploadFile(file) {
        let isUploadReject = false;

        const fileReader = new FileReader();

        fileReader.addEventListener(
            'load',
            () => {
                if (!isUploadReject) {
                    const base64ImageSrc = fileReader.result;
                    this.insertBase64Image(base64ImageSrc);
                }
            },
            false
        );

        if (file) {
            fileReader.readAsDataURL(file);
        }

        this.options.upload(file).then(
            ({ url }) => {
                this.insertToEditor(url);
            },
            (error) => {
                isUploadReject = true;
                this.removeBase64Image();
                console.error('GrispiUploader, error', error.message);
                const errorMessage = `${file.name} file upload failed, try again`;
                grispiNotification('error', errorMessage, 'error');
            }
        );
    }

    insertBase64Image(url) {
        const range = this.range;
        this.quill.insertEmbed(range.index, LoadingImage.blotName, `${url}`, 'user');
    }

    insertToEditor(url) {
        const range = this.range;
        // Delete the placeholder image
        this.quill.deleteText(range.index, 3, 'user');
        // Insert the server saved image
        this.quill.insertEmbed(range.index, 'image', `${url}`, 'user');

        range.index++;
        this.quill.setSelection(range, 'user');
    }

    removeBase64Image() {
        const range = this.range;
        this.quill.deleteText(range.index, 3, 'user');
    }
}

window.GrispiUploader = GrispiUploader;
export default GrispiUploader;
